// Report-feed language-model enrichment state: S-2 (intelligence officer) assessments on significant
// contact events, and SITREP (situation report) phrasing. Kept as its own store, separate from
// `src/state/store.ts` (the worker<->React bridge, extended concurrently by the planning stage work), per
// plan Task 18. Read-only imports from `store.ts` are fine; this module never writes to it.
//
// Pattern: spec §6 "grounded narration" - every call is grounded in real event ids and validated with
// `validateCitations` before it reaches the screen, and every call has a deterministic template fallback
// (`templateAssessment`) so a slow or unreachable model degrades to a labeled template line, never silence.

import { create } from 'zustand';
import { estimateContactEvent, type ContactEstimate } from '../ai/contactEstimate';
import { estimatorContextFromSnapshot, noteFirstSeen } from '../ai/copContext';
import { getEstimator } from '../ai/estimator';
import type { Event } from '../sim/types';
import { useStore } from './store';
import { useAdvisors } from './advisors';
import { callLLM } from '../ai/client';
import { AssessmentSchema } from '../ai/schemas';
import { validateCitations } from '../ai/validate';
import { templateAssessment, type PlayContext } from '../ai/templates';
import { buildS2Prompt, buildSitrepPrompt, type S2Context, type SitrepContext } from '../ai/feedPrompts';

export interface Enrichment {
  role?: ContactEstimate;
  text: string;
  confidence: 'low' | 'medium' | 'high';
  /** Event ids the model (or the template) actually cited, already filtered to known ids. */
  cited: number[];
  /**
   * 'llm': a genuinely new model answer. 'template': the deterministic fallback, unchanged. 'restated':
   * the model answered, but its wording was a near-verbatim restatement of the already-known template
   * text (`isRestatement` below) - the independent review's finding 5 ("five SITREPs restated by the
   * model for zero information"), quantified in docs/design/ai-patterns.md ("When narration does not
   * fit"). The card hides the line for this source rather than showing a paraphrase of a number the
   * player already read in the template report above it.
   */
  source: 'llm' | 'template' | 'restated';
  /** More than half the model's original citations were unknown ids (`validateCitations`). */
  flagged: boolean;
}

/** Lowercase word set for a Jaccard overlap comparison - punctuation stripped, order and repetition
 * ignored, since "restated" is about which facts appear, not phrasing. */
function wordSet(s: string): Set<string> {
  return new Set(s.toLowerCase().match(/[a-z0-9']+/g) ?? []);
}

/**
 * Jaccard word overlap between two strings: |intersection| / |union| of their lowercase word sets, 0..1.
 * Two empty strings are defined as fully overlapping (1); one empty and one not are defined as no
 * overlap (0), so an enrichment that came back blank is never mistaken for "reused every word of the
 * template".
 */
export function jaccardOverlap(a: string, b: string): number {
  const sa = wordSet(a);
  const sb = wordSet(b);
  if (sa.size === 0 && sb.size === 0) return 1;
  if (sa.size === 0 || sb.size === 0) return 0;
  let intersection = 0;
  for (const w of sa) if (sb.has(w)) intersection++;
  return intersection / (sa.size + sb.size - intersection);
}

/**
 * True when a model's answer is close enough to the deterministic template text that showing it adds
 * nothing (spec: "near-verbatim restatements of the template text (Jaccard word overlap > 0.7)"), lowered to > 0.6 after measurement. An
 * empty template (nothing to restate) is never a restatement.
 */
export function isRestatement(candidate: string, template: string): boolean {
  if (template.trim().length === 0) return false;
  return jaccardOverlap(candidate, template) > 0.6; // measured: real model restatements score 0.66–0.70 (docs/design/ai-patterns.md §8)
}

/** At most this many enrichment calls in flight at once, across S-2 assessments and SITREP phrasing. */
const MAX_IN_FLIGHT = 3;

/**
 * Events that arrived before the map finished loading, replayed once it has.
 *
 * Same rule, and the same reason, as the advisors' `mapReady` gate (src/state/advisors.ts): a model call
 * holds a same-origin connection for up to the whole timeout, MapLibre needs several of the browser's
 * six to fetch its worker and its tiles, and a feed that starts calling at H-hour wins that race and
 * leaves the map black until it times out. Kept short - a backlog of stale assessments is worth less
 * than the next contact - and it is a deferral, not a drop: the queue is flushed, in order, on release.
 */
const MAX_DEFERRED = 8;

function timeoutMs(): number {
  const raw = Number(import.meta.env.VITE_LLM_TIMEOUT_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : 6000;
}

function isSignificantContact(ev: Event): boolean {
  return ev.side === 0 && ev.significant && (ev.kind === 'contact.new' || ev.kind === 'nai.contact');
}

/** The kinds severe enough that the SITREP right after one is always worth enriching, whatever the force
 * summary says (`src/ui/feed/triage.ts` calls these "critical" in the feed's own severity table - kept
 * as its own small set here rather than imported, so `src/state/feed.ts` does not reach into `src/ui`). */
const SITREP_CRITICAL_KINDS = new Set<Event['kind']>(['unit.destroyed', 'unit.broken', 'culminated', 'obj.seized']);

/** Force-state summary a SITREP is judged against: unchanged since the previous SITREP means nothing new
 * to phrase, so the model is never asked (spec: "skip the model call for a SITREP when the force state
 * is unchanged ... same alive count, contacts count, phase, fuel within 2%"). */
interface SitrepSummary { alive: number; contacts: number; phase: string; fuel: number }

function sitrepSummaryUnchanged(a: SitrepSummary | null, b: SitrepSummary): boolean {
  if (!a) return false;
  return a.alive === b.alive && a.contacts === b.contacts && a.phase === b.phase && Math.abs(a.fuel - b.fuel) <= 2;
}

/** Enrichment-eligibility state for the periodic SITREP, module-level like `deferred` above: it tracks
 * the run's whole history of SITREPs, not any one component's mount lifetime. Reset only by a fresh page
 * load, which is also when a fresh run starts. */
const sitrepGate: { prevSummary: SitrepSummary | null; seenPhases: Set<string>; criticalSince: boolean } = {
  prevSummary: null,
  seenPhases: new Set(),
  criticalSince: false,
};

interface FeedState {
  enrichments: Record<number, Enrichment>;
  pending: Set<number>;
  inFlight: number;
  /** Requests an enrichment for one event (a real sim event, or a synthetic `{kind:'sitrep', ...}` built
   * by `src/ui/feed/ReportFeed.tsx` for its own periodic SITREP). A no-op for any other kind, for an
   * event already enriched or already pending, or once `MAX_IN_FLIGHT` calls are outstanding. */
  enrich: (ev: Event) => void;
}

const deferred: Event[] = [];
const firstSeen = new Map<number, number>();
let generation = 0;

export const useFeed = create<FeedState>((set, get) => ({
  enrichments: {},
  pending: new Set(),
  inFlight: 0,

  enrich(ev) {
    const isSitrep = ev.kind === 'sitrep';

    // Track "a critical event happened since the last SITREP" off every blue event the feed sees, not
    // just the ones this function goes on to act on - a broken platoon has to be noticed here even
    // though `unit.broken` is never itself enrichable.
    if (ev.side === 0 && SITREP_CRITICAL_KINDS.has(ev.kind)) sitrepGate.criticalSince = true;

    if (!isSitrep && !isSignificantContact(ev)) return;

    // The map comes first (see `MAX_DEFERRED`). Nothing is asked of the model until it is up. Deliberately
    // ahead of the SITREP eligibility check below: that check mutates `sitrepGate` once and for all, and a
    // deferred SITREP is replayed through this whole function again once the map is ready, so mutating
    // before the defer would make a re-run compare the gate against itself and wrongly call it unchanged.
    if (!useAdvisors.getState().mapReady) {
      if (deferred.some((d) => d.id === ev.id)) return;
      deferred.push(ev);
      if (deferred.length > MAX_DEFERRED) deferred.shift();
      return;
    }

    // "Enrichment only when it adds a fact": at most the first SITREP of each phase, plus any SITREP
    // that follows a critical event, and never one whose force summary has not moved since the last
    // SITREP - same alive count, same contacts count, same phase, fuel within 2 points. Independent
    // review finding 5: "five SITREPs restated by the model for zero information." This is the other
    // half of the fix from `isRestatement` above: that one catches a restatement that already came back
    // from the model, this one skips the call in the first place when nothing changed.
    if (isSitrep) {
      const snap = useStore.getState().snapshot;
      if (!snap) return;
      const summary: SitrepSummary = {
        alive: snap.units.filter((u) => u.side === 0 && u.alive).length,
        contacts: snap.picture.contacts.length,
        phase: snap.phase,
        fuel: snap.force.fuel,
      };
      const unchanged = sitrepSummaryUnchanged(sitrepGate.prevSummary, summary);
      const firstOfPhase = !sitrepGate.seenPhases.has(summary.phase);
      const eligible = firstOfPhase || sitrepGate.criticalSince;

      sitrepGate.seenPhases.add(summary.phase);
      sitrepGate.prevSummary = summary;
      sitrepGate.criticalSince = false;

      if (unchanged || !eligible) return;
    }

    const s = get();
    if (s.enrichments[ev.id] || s.pending.has(ev.id)) return;
    if (s.inFlight >= MAX_IN_FLIGHT) return;

    const live = useStore.getState();
    const { scenario, grid, snapshot, events, dials } = live;
    if (!scenario || !grid || !snapshot) return;

    set((prev) => {
      const pending = new Set(prev.pending);
      pending.add(ev.id);
      return { pending, inFlight: prev.inFlight + 1 };
    });

    // The last 10 events at or before the triggering one - what the model is actually shown, and the
    // set `validateCitations` treats as "known" so a citation to an event the model was never given
    // still gets dropped even if it happens to be a real id elsewhere in the log.
    const recentEvents = events.filter((e) => e.side === 0 && e.id <= ev.id).slice(-10);
    const known = new Set<number>([ev.id, ...recentEvents.map((e) => e.id)]);

    const playCtx: PlayContext = {
      t: snapshot.tick,
      units: snapshot.units,
      contacts: snapshot.picture.contacts,
      graphics: scenario.graphics,
      recentEvents,
      objectiveId: scenario.objectiveId,
      dials,
      grid,
    };

    const run = generation;
    const finish = (enrichment: Enrichment): void => {
      if (run !== generation) return;
      set((prev) => {
        const pending = new Set(prev.pending);
        pending.delete(ev.id);
        return {
          enrichments: { ...prev.enrichments, [ev.id]: enrichment },
          pending,
          inFlight: Math.max(0, prev.inFlight - 1),
        };
      });
    };

    // The reference text a real model answer is judged against for the restatement check below: for a
    // SITREP, the already-computed report line itself (what `buildSitrepPrompt` asked the model to
    // rephrase); for a significant contact, the same deterministic line the template fallback would have
    // shown. Cheap either way - `templateAssessment` is pure string-building, no model call.
    const templateText = isSitrep ? String(ev.payload.text ?? '') : templateAssessment(ev, playCtx).text;

    const request = isSitrep
      ? (() => {
          const ctx: SitrepContext = { recentEvents, phase: snapshot.phase };
          const { system, user } = buildSitrepPrompt(templateText, ctx);
          return callLLM(
            { model: 'haiku', system, user, schema: AssessmentSchema, timeoutMs: timeoutMs() },
            () => templateAssessment(ev, playCtx),
          );
        })()
      : (() => {
          const nais = scenario.graphics.filter((g) => g.kind === 'nai');
          const ctx: S2Context = {
            recentEvents, contacts: snapshot.picture.contacts, nais, enemyDesc: scenario.names.enemy, grid,
          };
          const { system, user } = buildS2Prompt(ev, ctx);
          return callLLM(
            { model: 'sonnet', system, user, schema: AssessmentSchema, timeoutMs: timeoutMs() },
            () => templateAssessment(ev, playCtx),
          );
        })();

    const roleRequest = isSitrep ? Promise.resolve(undefined) : estimateContactEvent(
      ev,
      estimatorContextFromSnapshot(snapshot, {
        dials, scenario, aoTitle: live.meta?.title,
        firstSeen: noteFirstSeen(firstSeen, snapshot.picture.contacts, snapshot.tick),
      }),
      getEstimator(dials),
    ).catch(() => undefined);

    Promise.all([request, roleRequest]).then(([res, role]) => {
      const { value, flagged } = validateCitations(res.value, known);
      // A genuine model answer that just restates the template in different words adds nothing a player
      // can act on - `isRestatement` catches it here, after validation, so a restatement with bad
      // citations still shows `flagged` (the citation problem is real regardless of the wording problem).
      const source: Enrichment['source'] = res.source === 'llm' && isRestatement(value.text, templateText)
        ? 'restated'
        : res.source;
      finish({ text: value.text, confidence: value.confidence, cited: value.cited_event_ids, source, flagged, role });
    });
  },
}));

// Release: the moment the stage's map reports itself loaded, whatever the feed held back goes out in the
// order it arrived. Subscribed once at module load, for the lifetime of the tab - `setMapReady(false)`
// on stage teardown re-arms the gate for the next stage's map without needing to re-subscribe.
useAdvisors.subscribe((s, prev) => {
  if (!s.mapReady || prev.mapReady) return;
  const queued = deferred.splice(0, deferred.length);
  for (const ev of queued) useFeed.getState().enrich(ev);
});

// A new worker means a new fight. Old model requests cannot populate the next run's event ids.
useStore.subscribe((state, previous) => {
  if (state.sim === previous.sim) return;
  generation++;
  deferred.length = 0;
  firstSeen.clear();
  sitrepGate.prevSummary = null;
  sitrepGate.seenPhases.clear();
  sitrepGate.criticalSince = false;
  useFeed.setState({ enrichments: {}, pending: new Set(), inFlight: 0 });
});
